import { useEffect, useMemo, useRef, useState } from "react";
import VimeoPlayerSDK from "@vimeo/player";
import { normalizeVimeoEmbed } from "../lib/vimeo";
import { SpadeMark } from "./LogoLoader";
import { useAudioMute } from "../lib/audioMute";

/**
 * Reliable background video player with a 3-layer fail-safe stack.
 *
 *   z-0 :  fallback image      (always mounted UNLESS premium=true)
 *   z-10:  MP4 <video> loop    (optional — e.g. Bunny.net CDN URL; always-on when provided)
 *   z-20:  Vimeo iframe        (optional — fades out if it fails to signal "ready" in `timeoutMs`)
 *
 * With this stack the hero is NEVER blank:
 *  - If Vimeo works, it plays on top.
 *  - If Vimeo is slow or down, it fades out → MP4 continues underneath.
 *  - If MP4 is absent and Vimeo fails, fallback image is visible.
 *  - If video_url is empty, iframe isn't mounted at all.
 *
 * Props:
 *   url             — Vimeo URL (any format; normalized client-side). Optional.
 *   mp4             — direct MP4 URL (Bunny.net CDN recommended). Optional.
 *   fallback        — fallback image URL. Strongly recommended for HERO use.
 *   orientation     — "horizontal" (default) | "vertical"
 *   timeoutMs       — Vimeo ready timeout before fading iframe (default 4000)
 *   premium         — When true: no fallback image flash. Shows only a clean
 *                     black canvas + minimal gold spinner until the video
 *                     signals ready, then fades the video in. Used for
 *                     non-hero contexts (case studies, blog, etc.) so we never
 *                     see a thumbnail-flash. Default false.
 *   testid          — base testid (default "vimeo-player")
 */
export default function VimeoPlayer({
  url,
  mp4,
  fallback,
  orientation = "horizontal",
  timeoutMs = 4000,
  premium = false,
  respectMute = false,      // When true, video honors the global AudioMute state
  inlineToggle = true,      // When false, don't render the built-in audio toggle button
  onPlayerReady,            // Callback fired with the @vimeo/player SDK instance after ready()
  visibilityFade = true,    // When true (default), audio fades based on viewport visibility. Set false for heroes that manage their own scroll-driven fade.
  objectFit = "contain",   // "contain" (default, Vimeo's native fit) | "cover" (fills container, crops overflow via SDK-detected aspect)
  className = "",
  testid = "vimeo-player",
}) {
  // Global mute state — only actually applied when respectMute=true.
  const { muted: globalMuted, toggle: toggleMute } = useAudioMute();
  // The iframe src ALWAYS starts muted (browser autoplay policy) — the SDK
  // flips real audio on/off after ready. Critically, embedUrl must NOT
  // depend on the live mute state, otherwise every toggle rebuilds the URL
  // and reloads the iframe (video restarts from the beginning).
  const embedUrl = useMemo(
    () => (url ? normalizeVimeoEmbed(url, { background: !respectMute, muted: true }) : ""),
    [url, respectMute]
  );
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const iframeRef = useRef(null);
  const mp4Ref = useRef(null);
  const sdkPlayerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [coverStyle, setCoverStyle] = useState(null);

  // Attach the Vimeo Player SDK whenever we need imperative control —
  // either to honor global mute, to detect video aspect for cover-fit, or
  // both. The SDK waits for the player's internal "ready" event before
  // delivering setMuted/getVideoWidth calls.
  //
  // iOS Safari NOTE: on SPA route change / hard reload iOS treats each new
  // page as a fresh autoplay context. Even though the user clicked a link
  // (a gesture!) to get here, that gesture doesn't count for THIS page's
  // playback session — so setMuted(false) is silently REJECTED and the
  // video stays muted while our button visual reads unmuted. Fix: after
  // calling setMuted, verify with getMuted; if iOS blocked us, register
  // a one-time page gesture listener that retries the unmute on first tap
  // (any tap on the page, including the video itself, counts).
  const needSDK = respectMute || objectFit === "cover";
  useEffect(() => {
    if (!needSDK || !iframeRef.current || !embedUrl) return undefined;
    let cancelled = false;
    let p;
    let unmuteRetryCleanup = null;

    const armUnmuteRetry = () => {
      if (unmuteRetryCleanup) return; // already armed
      const retry = () => {
        if (!sdkPlayerRef.current || cancelled) return;
        sdkPlayerRef.current.setMuted(false).catch(() => {});
        sdkPlayerRef.current.setVolume(1).catch(() => {});
      };
      const opts = { once: true, capture: true };
      window.addEventListener("pointerdown", retry, opts);
      window.addEventListener("touchstart", retry, opts);
      window.addEventListener("keydown", retry, opts);
      unmuteRetryCleanup = () => {
        window.removeEventListener("pointerdown", retry, opts);
        window.removeEventListener("touchstart", retry, opts);
        window.removeEventListener("keydown", retry, opts);
      };
    };

    try {
      p = new VimeoPlayerSDK(iframeRef.current);
      sdkPlayerRef.current = p;
      p.ready().then(async () => {
        if (cancelled) return;
        if (respectMute) {
          await p.setMuted(globalMuted).catch(() => {});
          await p.setVolume(globalMuted ? 0 : 1).catch(() => {});
          // Verify — iOS Safari may silently reject unmute-without-gesture.
          if (!globalMuted) {
            const actuallyMuted = await p.getMuted().catch(() => true);
            if (actuallyMuted) armUnmuteRetry();
          }
        }
        try { onPlayerReady && onPlayerReady(p); } catch {}
      }).catch(() => {});
    } catch {}
    return () => {
      cancelled = true;
      sdkPlayerRef.current = null;
      if (unmuteRetryCleanup) unmuteRetryCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needSDK, embedUrl]);

  // Cover-fit: SDK-detected aspect + ResizeObserver keeps the iframe sized
  // so the video fully covers its container (no letterboxing). Overflow on
  // the long axis is cropped by the parent's `overflow: hidden`.
  useEffect(() => {
    if (objectFit !== "cover") return undefined;
    const wrap = wrapperRef.current;
    if (!wrap) return undefined;
    let aspect = null;
    let cancelled = false;

    const applyCover = () => {
      if (!aspect || !wrap || cancelled) return;
      const r = wrap.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const containerAspect = r.width / r.height;
      let w;
      let h;
      if (containerAspect < aspect) {
        h = r.height;
        w = r.height * aspect;
      } else {
        w = r.width;
        h = r.width / aspect;
      }
      setCoverStyle({
        position: "absolute",
        top: "50%",
        left: "50%",
        width: `${w}px`,
        height: `${h}px`,
        transform: "translate(-50%, -50%)",
      });
    };

    const pullAspect = () => {
      const p = sdkPlayerRef.current;
      if (!p) return;
      Promise.all([p.getVideoWidth(), p.getVideoHeight()])
        .then(([vw, vh]) => {
          if (cancelled || !vw || !vh) return;
          aspect = vw / vh;
          applyCover();
        })
        .catch(() => {});
    };

    pullAspect();
    const poll = setInterval(() => {
      if (aspect) { clearInterval(poll); return; }
      pullAspect();
    }, 400);
    const stopPoll = setTimeout(() => clearInterval(poll), 8000);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(applyCover);
      ro.observe(wrap);
    } else {
      window.addEventListener("resize", applyCover);
    }
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(stopPoll);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", applyCover);
    };
  }, [objectFit, embedUrl]);

  // Push mute state to the SDK player + mp4 fallback whenever the global
  // toggle flips — no re-embed, no reload.
  useEffect(() => {
    if (!respectMute) return;
    const p = sdkPlayerRef.current;
    if (p) {
      p.setMuted(globalMuted).catch(() => {});
      p.setVolume(globalMuted ? 0 : 1).catch(() => {});
    }
    try {
      if (mp4Ref.current) mp4Ref.current.muted = globalMuted;
    } catch {}
  }, [globalMuted, respectMute]);

  // Viewport-visibility → audio fade. IntersectionObserver-only (no rAF,
  // no per-frame getBoundingClientRect). IO with 51 fine thresholds gives
  // ~2% steps as the block scrolls in/out; the 0.02 delta gate on
  // setVolume drops that to ~50 iframe postMessages across the whole
  // fade — imperceptible in audio, but zero per-frame layout reads on
  // the main thread during scroll.
  useEffect(() => {
    if (!respectMute || !visibilityFade) return undefined;
    const wrap = wrapperRef.current;
    if (!wrap) return undefined;
    let lastApplied = -1;

    const applyVolumeIfChanged = (target) => {
      if (Math.abs(target - lastApplied) < 0.02) return;
      lastApplied = target;
      const p = sdkPlayerRef.current;
      try { p && p.setVolume(target); } catch {}
      try { if (mp4Ref.current) mp4Ref.current.volume = target; } catch {}
    };

    const thresholds = Array.from({ length: 51 }, (_, i) => i / 50);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = (!entry.isIntersecting || globalMuted) ? 0 : entry.intersectionRatio;
        applyVolumeIfChanged(target);
      });
    }, { threshold: thresholds });
    io.observe(wrap);

    return () => {
      io.disconnect();
    };
  }, [respectMute, visibilityFade, globalMuted]);

  // Vimeo ready detection + timeout failover
  useEffect(() => {
    if (!embedUrl) {
      setFailed(true);
      return undefined;
    }
    setLoaded(false);
    setFailed(false);

    const onMessage = (e) => {
      try {
        if (!e.origin || !e.origin.includes("vimeo.com")) return;
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        const evt = data?.event || data?.method;
        if (evt === "ready" || evt === "play" || evt === "playing" || evt === "loaded") {
          setLoaded(true);
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("message", onMessage);

    const t = setTimeout(() => {
      setLoaded((prev) => {
        if (!prev) setFailed(true);
        return prev;
      });
    }, timeoutMs);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(t);
    };
  }, [embedUrl, timeoutMs]);

  // Tap-to-play on mobile (autoplay may be blocked)
  const attemptPlay = () => {
    try {
      mp4Ref.current?.play?.();
    } catch {}
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ method: "play" }),
        "*"
      );
    } catch {}
  };

  const iframeStyle = {
    border: 0,
    // In premium mode, keep iframe invisible until Vimeo signals ready, so
    // its native "LOADING…" chrome never bleeds through over our brand loader.
    opacity: failed ? 0 : (premium ? (loaded ? 1 : 0) : 1),
    transition: "opacity 700ms ease",
    // Pre-promote the iframe itself to a GPU compositor layer so Safari
    // doesn't pay the one-time "wake-up" cost when it first scrolls into
    // viewport — that was the residual scroll-up stutter on Mac trackpad.
    // translate3d is the historical trigger; translateZ works too.
    transform: "translate3d(0, 0, 0)",
    WebkitTransform: "translate3d(0, 0, 0)",
    // When objectFit=cover is active AND we've computed a coverStyle, spread
    // it here so the iframe is sized to fully cover its container.
    // coverStyle wins because it may also carry a transform (translate(-50%,-50%));
    // in that case Safari still promotes to a compositor layer thanks to the
    // non-identity transform.
    ...(coverStyle || {}),
  };

  const fallbackImgLayer = (extraStyle = {}) => {
    if (premium) {
      // Premium mode: clean black canvas + brand spade-mark loader (matches
      // the global LogoLoader) until video signals ready, then fades out.
      return (
        <div
          data-testid={`${testid}-loading`}
          aria-hidden="true"
          className="absolute inset-0 bg-black flex items-center justify-center z-[1]"
          style={{
            opacity: loaded ? 0 : 1,
            transition: "opacity 700ms ease",
            pointerEvents: loaded ? "none" : "auto",
            ...extraStyle,
          }}
        >
          <SpadeMark size="compact" />
        </div>
      );
    }
    return (
      <div
        data-testid={`${testid}-fallback`}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: fallback ? `url('${fallback}')` : undefined,
          backgroundColor: fallback ? undefined : "#0A0A0C",
          ...extraStyle,
        }}
        aria-hidden="true"
      />
    );
  };

  const mp4Layer = mp4 ? (
    <video
      ref={mp4Ref}
      data-testid={`${testid}-mp4`}
      src={mp4}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      onError={() => {
        /* graceful — mp4 failing still leaves image layer visible */
      }}
    />
  ) : null;

  const iframeLayer = embedUrl ? (
    <iframe
      ref={iframeRef}
      data-testid={`${testid}-iframe`}
      data-loaded={loaded ? "1" : "0"}
      data-failed={failed ? "1" : "0"}
      src={embedUrl}
      title="Video"
      className={coverStyle ? "pointer-events-none" : "absolute inset-0 w-full h-full pointer-events-none"}
      style={iframeStyle}
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      loading="eager"
      onError={() => setFailed(true)}
    />
  ) : null;

  // Inline audio mute toggle — only rendered for mute-respecting videos so
  // it "sticks" to the video (moves with the video during scroll, unlike
  // the previous fixed-viewport button). Positioned bottom-LEFT of the
  // video, so it never collides with the site-wide WhatsApp CTA (bottom-right).
  const muteToggleLayer = respectMute && inlineToggle ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleMute(); }}
      aria-label={globalMuted ? "Turn video sound on" : "Turn video sound off"}
      aria-pressed={!globalMuted}
      data-testid="audio-mute-toggle"
      className={`absolute bottom-16 left-4 md:bottom-20 md:left-6 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border pointer-events-auto ${
        globalMuted
          ? "bg-black/50 border-white/25 text-white/80 hover:text-white hover:border-white/60"
          : "bg-[#C9A050] border-[#C9A050] text-black hover:brightness-110"
      }`}
    >
      {globalMuted ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <line x1="23" y1="9" x2="17" y2="15" />
          <line x1="17" y1="9" x2="23" y2="15" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
      )}
    </button>
  ) : null;

  if (orientation === "vertical") {
    return (
      <div
        data-testid={`${testid}-wrap`}
        data-orientation="vertical"
        className={`video-wrapper ${className}`}
        onClick={attemptPlay}
      >
        {fallback && fallback !== "-" && (
          <div
            data-testid={`${testid}-bg`}
            aria-hidden="true"
            className="video-bg"
            style={{ backgroundImage: `url('${fallback}')` }}
          />
        )}
        <div className="video-main">
          {fallbackImgLayer()}
          {mp4Layer}
          {iframeLayer}
          <div className="absolute inset-0 pointer-events-none ring-1 ring-white/5" />
          {muteToggleLayer}
        </div>
      </div>
    );
  }

  // horizontal
  return (
    <div
      ref={wrapperRef}
      data-testid={`${testid}-wrap`}
      data-orientation="horizontal"
      className={`absolute inset-0 overflow-hidden ${className}`}
      onClick={attemptPlay}
    >
      {fallbackImgLayer()}
      {mp4Layer}
      {iframeLayer}
      {muteToggleLayer}
    </div>
  );
}
